import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import {
  ActionSheetController,
  IonItemSliding,
  ModalController,
  Platform,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, of, take } from 'rxjs';

import {
  AttendeeInformation,
  CalendarEvent,
  DATETIMEFORMART,
  EventRequest,
  UnleashFeatureFlags,
} from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { LanguageService } from 'src/app/services/languageservice/language.service';
import { checkIsNextDay } from 'src/app/utils/utils';
import * as moment from 'moment';
import { StringInfoMessageService } from 'src/app/services/string-info-message/string-info-message.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { PlacesService } from 'src/app/services/placesService/places.service';
import { FloorplanSearchService } from '../search/floorplan-search.service';
import { EventComponent } from '../event/event.component';
import { ViewEditBookingComponent } from '../view-edit-booking/view-edit-booking.component';
import { EVENTPAGETYPES } from '../place/place.component';
import { BookingsStore } from 'src/app/stores/bookings.store';
import { UserStore } from 'src/app/stores/user.store';
import { AttendeesStore } from 'src/app/stores/attendees.store';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { UnleashService } from 'src/app/services/unleash/unleash.service';
export type InteractionMode = 'full' | 'readOnly';

export type TimeUntilStartSegment = {
  key: string;
  count: number;
};
@Component({
  selector: 'app-my-bookings-item',
  templateUrl: './my-bookings-item.component.html',
  styleUrls: ['./my-bookings-item.component.scss'],
})
export class MyBookingsItemComponent implements OnDestroy {
  calendarEvent = input<any>();

  @Input() selectedSegment;
  @Input() interactionMode: InteractionMode = 'full';

  @ViewChild('popover') popover;
  @ViewChild('slidingItem') slidingItem: IonItemSliding;
  @ViewChild('SeriesPopOver') SeriesPopOver;
  unleashFeatureFlags = UnleashFeatureFlags;

  public locale = this.dateTimeFormatService.selectedDateTimeFormat;
  public readonly meetingEndingSoonThreshold = 15;
  public readonly meetingStartingSoonThreshold = 15 ;
  public isExtendMeetingPopoverOpen = false;
  isLoading: boolean = false;
  // myResponseToEvent: string;
  // isSeries: boolean = false;
  noAddressSpecified: boolean = false;
  address = computed(() =>
    this.stringInfoMessageService.buildAddressString(this.calendarEvent().place)
  );
  isNextDay = computed(() =>
    checkIsNextDay(this.calendarEvent().startTime, this.calendarEvent().endTime)
  );
  extendedAttendees = computed(() =>
    this.attendeesStore.extendAttendeeInformation(
      this.calendarEvent().attendees
    )
  );

  organizer = computed(() => {
    return this.extendedAttendees().find(
      (attendee) => attendee.userPrincipalName == this.calendarEvent().organizer?.emailAddress || ''
    );
  });

  userResponse = computed(() => {
    return this.extendedAttendees().find(
      (attendee) => attendee.userPrincipalName == this.userStore.user()?.mail
    )?.response;
  });

  acceptedAttendees = computed(() =>
    this.extendedAttendees().filter(
      (attendee) => attendee.response.toLowerCase() === 'accepted'
    )
  );
  declinedAttendees = computed(() =>
    this.extendedAttendees().filter(
      (attendee) => attendee.response.toLowerCase() === 'declined'
    )
  );
  pendingAttendees = computed(() =>
    this.extendedAttendees().filter((attendee) => {
      const response = attendee.response.toLowerCase();
      return (
        (response === 'none' || response === 'notresponded') &&
        attendee.userPrincipalName !==
          this.calendarEvent().organizer.emailAddress
      );
    })
  );
  tentativelyAttendees = computed(() =>
    this.extendedAttendees().filter(
      (attendee) => attendee.response.toLowerCase() === 'tentativelyaccepted'
    )
  );
  subjectFirstSegment = computed(() => {
    if (this.calendarEvent().isCancelled) {
      const subject = this.calendarEvent().subject as string;
      if (subject.includes('Canceled') || subject.includes('Abgesagt')) {
        const index = subject.indexOf(':');
        const subjectSplitted =
          index !== -1
            ? [subject.substring(0, index), subject.substring(index + 1)]
            : [subject];
        return subjectSplitted[0] + ':';
      }
    }
    return null;
  });
  subjectSecondSegment = computed(() => {
    if (this.calendarEvent().isCancelled) {
      const subject = this.calendarEvent().subject as string;
      if (subject.includes('Canceled') || subject.includes('Abgesagt')) {
        const index = subject.indexOf(':');
        const subjectSplitted =
          index !== -1
            ? [subject.substring(0, index), subject.substring(index + 1)]
            : [subject];
        return subjectSplitted[1] && subjectSplitted[1].trim() !== ''
          ? subjectSplitted[1]
          : null;
      }
    }
    return null;
  });
  isMeetingEndingSoon = computed(() => {
    if (this.calendarEvent().resourceStatus !== 'Accepted') return false;
    const now = moment();
    const startTime = moment(this.calendarEvent().startTime);
    const endTime = moment(this.calendarEvent().endTime);
    if (now.isAfter(startTime)) {
      const minutes = endTime.diff(now, 'minutes') + 1; // because the different is not including the current minute
      return minutes <= this.meetingEndingSoonThreshold && minutes > 0 && this.calendarEvent().isOrganizer;
    }
    return false;
  });
  minutesRemaining = computed(() => {
    if (this.calendarEvent().resourceStatus !== 'Accepted') return 0;
    const now = moment();
    const startTime = moment(this.calendarEvent().startTime);
    const endTime = moment(this.calendarEvent().endTime);
    if (now.isAfter(startTime)) {
      return endTime.diff(now, 'minutes') + 1;
    }
    return 0;
  });
  checkInEnabled = computed(() => {
    const event = this.calendarEvent();
    
    const eventStart = new Date(event.startTime).getTime();
    const now = Date.now();
    const diffMinutes = (eventStart - now) / 60000;

    const eventAlreadyStarted = now >= eventStart;
    const preCheckInAllowed =
      event.place?.preCheckInEnabled &&
      diffMinutes <= event.place.preCheckInTime;
    const durationRemainingLessThanOneMinute = (new Date(event.endTime).getTime() - now) / 60000 <= 1;
    
    return (
      !this.isReadOnly &&
      event.resourceStatus == 'Accepted' &&
      !event.isCancelled &&
      !event.checkedIn &&
      !event.place?.checkOutOnlyEnabled &&
      !durationRemainingLessThanOneMinute &&
      (eventAlreadyStarted || preCheckInAllowed)
    );
  });
  checkOutEnabled = computed(() => {
    const event = this.calendarEvent();
    
    const now = Date.now();
    const durationRemainingLessThanOneMinute = (new Date(event.endTime).getTime() - now) / 60000 <= 1;
    
    return (
      this.calendarEvent().isOrganizer &&
      event.resourceStatus == 'Accepted' &&
      !event.isCancelled &&
      event.checkedIn &&
      !durationRemainingLessThanOneMinute &&
      !this.isCheckedOut
    );
  });

  private timeUntilStartTick = signal(Date.now());
  private timeUntilStartInterval: ReturnType<typeof setInterval> | null = null;
  private readonly timeUntilStartRefreshMs = 60_000;

  /** True while start time is still in the future (meeting has not started yet). */
  meetingHasNotStarted = computed(() => {
    const event = this.calendarEvent();
    if (!event?.startTime) {
      return false;
    }
    return moment(event.startTime).isAfter(moment(this.timeUntilStartTick()));
  });
isMeetingStartingSoon = computed(() => {
  if (!this.meetingHasNotStarted()) {
    return false;
  }
  const minutes = moment(this.calendarEvent().startTime).diff(
    moment(this.timeUntilStartTick()),
    'minutes'
  );
  return minutes <= this.meetingStartingSoonThreshold;
});

  showTimeUntilStart = computed(() => {
    const event = this.calendarEvent();
    if (!event || event.isCancelled || event.isExpired) {
      return false;
    }
    if (this.calendarEvent().checkedIn) {
      return false;
    }
    return this.meetingHasNotStarted();
  });

  timeUntilStartSegments = computed((): TimeUntilStartSegment[] => {
    if (!this.showTimeUntilStart()) {
      return [];
    }
    const duration = moment.duration(
      moment(this.calendarEvent().startTime).diff(moment(this.timeUntilStartTick()))
    );
    const totalMinutes = Math.max(0, Math.floor(duration.asMinutes()));
    const minutesPerDay = 24 * 60;
    const minutesPerWeek = 7 * minutesPerDay;

    const segments: TimeUntilStartSegment[] = [];
    const pushUnit = (count: number, singularKey: string, pluralKey: string) => {
      if (count > 0) {
        segments.push({
          key: count === 1 ? singularKey : pluralKey,
          count,
        });
      }
    };

    if (totalMinutes > minutesPerWeek) {
      pushUnit(
        Math.floor(totalMinutes / minutesPerWeek),
        'timeUntilStart.week',
        'timeUntilStart.weeks'
      );
    } else if (totalMinutes > minutesPerDay) {
      pushUnit(
        Math.floor(totalMinutes / minutesPerDay),
        'timeUntilStart.day',
        'timeUntilStart.days'
      );
    } else {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      pushUnit(hours, 'timeUntilStart.hour', 'timeUntilStart.hours');
      if (minutes > 0 || segments.length === 0) {
        pushUnit(minutes, 'timeUntilStart.minute', 'timeUntilStart.minutes');
      }
    }

    return segments;
  });

  extendMeetingAvailibility: any = {
    '15': false,
    '30': false,
    '45': false,
    '60': false,
  };
  extendMeetingAvailibilityError: string = '';
  extendMeetingAvailibilityLoading: boolean = false;
  isCheckedOut: boolean = false;
  checkInLoading: boolean = false;
  visibleAttendeeAvatars = computed(() => {
    const event = this.calendarEvent();
    const organizerName =
      event?.organizer?.name || event?.organizer?.emailAddress || '';
    const organizerKey = `org-${event?.id || organizerName}`;
    const organizerAvatar = {
      key: organizerKey,
      name: organizerName,
      userAvatarUrl: this.attendeesStore.findAttendeeAvatar(event?.organizer?.emailAddress || ''),
      isOrganizer: true,
    };

    const attendees = (this.extendedAttendees() || []).filter((attendee) => {
      const attendeeAny = attendee as any;
      const attendeeAddress = (
        attendee.userPrincipalName ||
        attendeeAny.emailAddress ||
        ''
      ).toLowerCase();
      const organizerAddress = (
        event?.organizer?.emailAddress ||
        ''
      ).toLowerCase();
      return attendeeAddress !== organizerAddress;
    });

    const attendeeAvatars = attendees.map((attendee) => {
      const attendeeAny = attendee as any;
      return {
        key:
          attendee.userPrincipalName ||
          attendeeAny.emailAddress ||
          `${attendee.firstName || ''}-${attendee.lastName || ''}`,
        name:
          attendeeAny.name ||
          `${attendee.firstName || ''} ${attendee.lastName || ''}`.trim(),
        userAvatarUrl: this.attendeesStore.findAttendeeAvatar(attendee.userPrincipalName || attendeeAny.emailAddress || ''),
        isOrganizer: false,
      };
    });

    return [organizerAvatar, ...attendeeAvatars].filter((avatar) => !!avatar.name);
  });
  attendeeOverflowCount = computed(() =>
    Math.max(this.visibleAttendeeAvatars().length - 3, 0)
  );

  invitedByOrganizerDisplayName = computed(() => {
    const o = this.calendarEvent()?.organizer;
    if (!o) {
      return '';
    }
    return (o.displayName || o.name || o.emailAddress || '').trim();
  });

  showInvitedByOnCard = computed(() => {
    const ev = this.calendarEvent();
    return !!ev && !ev.isOrganizer && !!this.invitedByOrganizerDisplayName();
  });

  constructor(
    public platform: Platform,
    private languageService: LanguageService,
    private stringInfoMessageService: StringInfoMessageService,
    private router: Router,
    public actionSheetController: ActionSheetController,
    private translateService: TranslateService,
    private apiService: ApiService,
    private cgf: ChangeDetectorRef,
    private dateTimeFormatService: DatetimeFormatService,
    private mdlCtrl: ModalController,
    private floorplanSearchService: FloorplanSearchService,
    private bookingsStore: BookingsStore,
    private userStore: UserStore,
    private attendeesStore: AttendeesStore,
    private toast: ToastService,
    private toastService: ToastService,
     public unleashService: UnleashService,
  ) {
    effect(() => {
      this.calendarEvent(); 
      // reset sliding item state on calendar event updates to prevent blank card on subsequent swipe interactions
      this.slidingItem?.closeOpened();
    });

    effect(
      () => {
        if (this.showTimeUntilStart()) {
          if (this.timeUntilStartInterval === null) {
            this.startTimeUntilStartRefresh();
          }
        } else {
          this.stopTimeUntilStartRefresh();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnDestroy(): void {
    this.stopTimeUntilStartRefresh();
  }

  private startTimeUntilStartRefresh(): void {
    this.timeUntilStartTick.set(Date.now());
    this.timeUntilStartInterval = setInterval(() => {
      this.timeUntilStartTick.set(Date.now());
    }, this.timeUntilStartRefreshMs);
  }

  private stopTimeUntilStartRefresh(): void {
    if (this.timeUntilStartInterval !== null) {
      clearInterval(this.timeUntilStartInterval);
      this.timeUntilStartInterval = null;
    }
  }

  async acceptEvent(eventId, isSeriesAction = false) {
    this.slidingItem.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('acceptSeriesMeetingQuestion')
        : this.translateService.instant('acceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.acceptInvitation(eventId, isSeriesAction).subscribe({
        next: () => {},
        error: (ex) => {
          this.toast.openErrorToastAsync(ex.error.message);
        },
      });
    }
  }

  async declineEvent(eventId, isSeriesAction = false) {
    this.slidingItem.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('declineSeriesMeetingQuestion')
        : this.translateService.instant('declineMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.declineInvitation(eventId, isSeriesAction).subscribe({
        next: () => {},
        error: (ex) => {
          this.toast.openErrorToastAsync(ex.error.message);
        },
      });
    }
  }

  async tentativelyAcceptEvent(eventId, isSeriesAction = false) {
    this.slidingItem.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant(
            'tentativelyAcceptSeriesMeetingQuestion'
          )
        : this.translateService.instant('tentativelyAcceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore
        .tentativelyAcceptInvitation(eventId, isSeriesAction)
        .subscribe({
          next: () => {},
          error: (ex) => {
            this.toast.openErrorToastAsync(ex.error.message);
          },
        });
    }
  }

  async showEditBookingModal() {
    const event = this.calendarEvent();
    if (this.unleashService.isEnabled(UnleashFeatureFlags.BOOKING_CHANGE_DESIGN)) {
      const modal: HTMLIonModalElement = await this.mdlCtrl.create({
        component: ViewEditBookingComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          attendees: event.attendees,
          place: event.place,
          meetingToEdit: event,
        },
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.action === 'declined' && data?.eventId) {
        this.bookingsStore.getMyBookings();
      }
    } else {
      const modal: HTMLIonModalElement = await this.mdlCtrl.create({
        component: EventComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          finalChoosenAttendees: this.extendedAttendees(),
          place: event.place,
          purpose: 'edit',
          meetingToEdit: event,
        },
      });
      modal.present();
    }
  }

  async showReadOnlyBookingModal() {
    const event = this.calendarEvent();
    if (this.unleashService.isEnabled(UnleashFeatureFlags.BOOKING_CHANGE_DESIGN)) {
      const modal: HTMLIonModalElement = await this.mdlCtrl.create({
        component: ViewEditBookingComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          attendees: event.attendees,
          place: event.place,
          meetingToEdit: event,
        },
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.action === 'declined' && data?.eventId) {
        this.bookingsStore.getMyBookings();
      }
    } else {
      const modal: HTMLIonModalElement = await this.mdlCtrl.create({
        component: EventComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          finalChoosenAttendees: event.attendees,
          place: event.place,
          purpose: 'readOnlyBooking',
          meetingToEdit: event,
        },
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.action === 'declined' && data?.eventId) {
        this.bookingsStore.getMyBookings();
      }
    }
  }

  async doDeleteBooking(id: string, isSeriesAction: boolean = false) {
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('deleteSeriesBookingQuestion')
        : this.translateService.instant('deleteBookingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: isSeriesAction
            ? this.translateService.instant('deleteSeriesBooking')
            : this.translateService.instant('deleteBooking'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('cancelButton'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.deleteBooking(id, isSeriesAction).subscribe({
        next: () => {},
        error: (ex) => {
          this.toast.openErrorToastAsync(ex.error.message);
        },
      });
    }
  }


  buildSeriesInformation(recurrencePattern) {
    return this.stringInfoMessageService.seriesEventInformationMessage(
      recurrencePattern,
      true
    );
  }

  async chooseIfSeriesToast(type) {
    this.slidingItem.closeOpened();
    let actionSheetButtons = [
      {
        text: this.translateService.instant('series.EventOccurrence'),
        role: 'occurrence',
      },
      {
        text: this.translateService.instant('series.EventSeries'),
        role: 'series',
      },
      {
        text: this.translateService.instant('cancelButton'),
        role: 'cancel',
      },
    ];

    let actionSheetHeader = this.translateService.instant(
      'series.' + type + 'SeriesEventHeaderModal'
    );

    const actionSheet = await this.actionSheetController.create({
      header: actionSheetHeader,
      mode: 'ios',
      buttons: actionSheetButtons,
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    this.chooseMatchingAction(type, condition.role);
  }
  chooseMatchingAction(type, role) {
    switch (type) {
      case 'delete':
        if (role === 'occurrence') {
          this.doDeleteBooking(this.calendarEvent().id, false);
        }
        if (role === 'series') {
          this.doDeleteBooking(this.calendarEvent().seriesMasterId, true);
        }
        break;
      case 'accept':
        if (role === 'occurrence') {
          this.acceptEvent(this.calendarEvent().id, false);
        }
        if (role === 'series') {
          this.acceptEvent(this.calendarEvent().seriesMasterId, true);
        }
        break;
      case 'tentativelyAccept':
        if (role === 'occurrence') {
          this.tentativelyAcceptEvent(this.calendarEvent().id, false);
        }
        if (role === 'series') {
          this.tentativelyAcceptEvent(
            this.calendarEvent().seriesMasterId,
            true
          );
        }
        break;
      case 'decline':
        if (role === 'occurrence') {
          this.declineEvent(this.calendarEvent().id, false);
        }
        if (role === 'series') {
          this.declineEvent(this.calendarEvent().seriesMasterId, true);
        }
        break;
      case 'edit':
        if (role === 'occurrence') {
          this.calendarEvent().editMasterEvent = false;
          this.showEditBookingModal();
        }
        if (role === 'series') {
          this.calendarEvent().editMasterEvent = true;
          this.showEditBookingModal();
        }
        break;
      default:
        break;
    }
  }

  joinMeeting(url: string) {
    window.open(url, '_blank');
  }

  openMap(place) {
    this.floorplanSearchService.openMap(place);
  }

  toggleExtendMeetingPopover(event?: Event) {
    event?.stopPropagation();
    this.isExtendMeetingPopoverOpen = !this.isExtendMeetingPopoverOpen;
    if (this.isExtendMeetingPopoverOpen) {
      this.extendMeetingAvailibilityLoading = true;
      this.extendMeetingAvailibilityError = '';
      if (!this.calendarEvent().place) {
        this.extendMeetingAvailibility = {
          '15': true,
          '30': true,
          '45': true,
          '60': true,
        };
        return;
      }
      const startTime = moment(this.calendarEvent().endTime)
        .add(1, 'minutes')
        .utc()
        .format(DATETIMEFORMART);
      const endTime = moment(this.calendarEvent().endTime)
        .add(1, 'hours')
        .utc()
        .format(DATETIMEFORMART);
      this.apiService
        .getAvailibilityToExtendMeeting(
          this.calendarEvent().place?.emailAddress,
          startTime,
          endTime,
          this.calendarEvent().place?.isOnPrem
        )
        .subscribe({
          next: (res) => {
            this.extendMeetingAvailibility = res;

            if (!res) {
              this.extendMeetingAvailibilityError =
                this.translateService.instant('error fetching availability');
            } else if (Object.values(res).every((value) => value === false)) {
              this.extendMeetingAvailibilityError =
                this.translateService.instant('slotsBooked');
            } else {
              this.extendMeetingAvailibilityError = null;
            }

            this.extendMeetingAvailibilityLoading = false;
          },
          error: (err) => {
            this.toast.openErrorToastAsync(err.message);
            this.extendMeetingAvailibilityLoading = false;
          },
        });
    }
  }

  extendMeeting(minutes: number) {
    this.extendMeetingAvailibilityLoading = true;
    const newEndTime = moment(this.calendarEvent().endTime)
      .add(minutes, 'minutes')
      .format(DATETIMEFORMART);

    try {
      const eventRequest: EventRequest =
        this.mapCalendarEventIntoEventRequest(newEndTime);
      this.bookingsStore.editBooking(this.calendarEvent().id, false, eventRequest)
        .pipe(
          catchError((error) => {
            this.toast.openErrorToastAsync(error.error.message);
            return of(false);
          })
        )
        .subscribe((res) => {
          this.isExtendMeetingPopoverOpen = false;
          this.extendMeetingAvailibilityLoading = false;
        });
    } catch (error) {
      console.error('Failed to extend meeting:', error);
      this.extendMeetingAvailibilityError = this.translateService.instant(
        'error extending meeting'
      );
      this.extendMeetingAvailibilityLoading = false;
    }
  }

  private mapCalendarEventIntoEventRequest(newEndTime: string = null): EventRequest {
    const recurrancePattern =
      this.calendarEvent().recurrencePattern?.accuranceType == null
        ? null
        : this.calendarEvent().recurrencePattern;
    return {
      showAs: this.calendarEvent().showAs,
      subject: this.calendarEvent().subject,
      startTime: moment(this.calendarEvent().startTime).format(DATETIMEFORMART),
      endTime: newEndTime ?? moment(this.calendarEvent().endTime).format(DATETIMEFORMART),
      isOnPrem: this.calendarEvent().place?.isOnPrem,
      attendees: this.extendedAttendees(),
      placeEmailAddress: this.calendarEvent().place?.emailAddress,
      recurrencePattern: recurrancePattern,
      reminderMinutesBeforeStart:
        this.calendarEvent().reminderMinutesBeforeStart,
      isOnlineMeeting: this.calendarEvent().isOnlineMeeting,
      timeZoneInfo: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  checkIn(event?: Event) {
    event?.stopPropagation();
    this.checkInLoading = true;
    let eventRequest = this.mapCalendarEventIntoEventRequest();
    this.apiService.checkIn(this.calendarEvent().id, eventRequest).pipe(
      catchError((error) => {
        this.toastService.openErrorToastAsync("checkInError");
        return of(false);
      })
    ).subscribe((res)=>{
      if(res){
        this.bookingsStore.checkInBookingLocal(this.calendarEvent());
      }
    })
  }

  checkOut(event?: Event) {
    event?.stopPropagation();
    this.checkInLoading = false;
    this.calendarEvent().isLoading = true;
    const eventRequest = this.mapCalendarEventIntoEventRequest();
    this.apiService.checkOut(this.calendarEvent().id, eventRequest).pipe(
      catchError((error) => {
        this.toastService.openErrorToastAsync("checkOutError");
        return of(false);
      })
    ).subscribe((res)=>{
      this.isCheckedOut = res;
      this.calendarEvent().isLoading = false;
    })
  }
  attendeeInitials(name: string): string {
    if (!name) return '?';
    const parts = name
      .split(' ')
      .map((segment) => segment.trim())
      .filter(Boolean)
      .slice(0, 2);
    if (parts.length === 0) return '?';
    return parts.map((segment) => segment[0]?.toUpperCase()).join('');
  }
  get isReadOnly() { return this.interactionMode === 'readOnly'; }
}